/**
 * src/components/agent/VoiceModeOverlay.styles.js
 *
 * Phase 82-05 — full-screen Voice Mode overlay. Tokens only (src/styles.js).
 */

export const overlayRootStyle = (drivingMode) => ({
  position: 'fixed',
  inset: 0,
  zIndex: 10000,
  background: 'var(--grad)',
  color: 'var(--t1)',
  display: 'flex',
  flexDirection: 'column',
  // 2026-04-20 iPhone 15 Pro margin fix — `env(safe-area-inset-*)` wraps
  // content around the dynamic island + the right-edge rounded-corner
  // cutout. Without these, user bubbles (aligned flex-end) got clipped
  // against the physical bezel. max() guarantees a 12px minimum even on
  // devices without safe-area support.
  paddingLeft: 'max(12px, env(safe-area-inset-left))',
  paddingRight: 'max(12px, env(safe-area-inset-right))',
  // 2026-06-09 island-collision fix (recurring) — the top bar (back chevron /
  // "Voice Mode" title / driving toggle) is this column's first child, so the
  // root's top padding is the SOLE source of the status-bar/Dynamic-Island
  // offset (the top bar must NOT also add the inset, or it double-counts).
  // Mirroring left/right, `max()` guarantees a non-zero floor: on devices /
  // PWA contexts where `env(safe-area-inset-top)` resolves to 0 (e.g. some
  // black-translucent standalone states) the bar would otherwise sit at y=0
  // and collide with the time + WiFi/battery. The 8px floor clears the bar of
  // the island; a larger device inset still wins.
  paddingTop: 'max(8px, env(safe-area-inset-top))',
  paddingBottom: drivingMode
    ? 'calc(32px + env(safe-area-inset-bottom))'
    : 'calc(16px + env(safe-area-inset-bottom))',
  overflow: 'hidden',
});

export const topBarStyle = (drivingMode) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: drivingMode ? '14px 20px' : '10px 16px',
  background: 'var(--glass)',
  borderBottom: '1px solid var(--bdr)',
  minHeight: drivingMode ? 64 : 56,
});

export const topBarTitleStyle = {
  flex: 1,
  fontSize: 15,
  fontWeight: 600,
  color: 'var(--t1)',
  letterSpacing: 0.2,
  textAlign: 'center',
};

export const chromeButtonStyle = (drivingMode) => ({
  minWidth: drivingMode ? 56 : 44,
  minHeight: drivingMode ? 56 : 44,
  padding: 0,
  borderRadius: 12,
  background: 'var(--glass)',
  color: 'var(--t1)',
  border: '1px solid var(--bdr)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
});

// 2026-04-20 (session 5) — CEO report: "there is no scroll on the text".
// Prior `flex: '0 0 auto'` had no height cap, no overflow, no scroll. Turns
// accumulated unbounded, pushing the orb off-screen. Now bounded to ~40vh
// with vertical scroll; the orb keeps its `flex: 1` share below. A consumer-
// side effect scrolls to bottom when the turn count changes so the latest
// reply is always in view without manual scrolling.
export const transcriptAreaStyle = {
  flex: '0 1 auto',
  maxHeight: '40vh',
  minHeight: 0,
  overflowY: 'auto',
  overflowX: 'hidden',
  padding: '8px 4px',
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  // Soft fade at the top so overflowed content tapers off instead of cutting.
  maskImage: 'linear-gradient(to bottom, transparent 0, black 16px, black 100%)',
  WebkitMaskImage: 'linear-gradient(to bottom, transparent 0, black 16px, black 100%)',
};

export const orbAreaStyle = (drivingMode) => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: drivingMode ? 24 : 16,
  position: 'relative',
  gap: drivingMode ? 24 : 16,
});

export const bottomBarStyle = (drivingMode) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 8,
  padding: drivingMode ? '16px 20px' : '10px 14px',
  background: 'var(--glass)',
  borderTop: '1px solid var(--bdr)',
  flexWrap: 'wrap',
});

export const bottomClusterStyle = {
  display: 'flex',
  gap: 8,
  alignItems: 'center',
};

export const controlBtnStyle = (drivingMode, active) => ({
  minWidth: drivingMode ? 56 : 44,
  minHeight: drivingMode ? 56 : 44,
  padding: '0 14px',
  borderRadius: 12,
  background: active ? 'var(--accent-bg)' : 'var(--glass)',
  color: active ? 'var(--accent)' : 'var(--t1)',
  border: '1px solid var(--bdr)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
  cursor: 'pointer',
  fontSize: 13,
  fontWeight: 600,
});

export const endBtnStyle = (drivingMode) => ({
  minWidth: drivingMode ? 80 : 64,
  minHeight: drivingMode ? 56 : 44,
  padding: '0 16px',
  borderRadius: 12,
  background: 'var(--bad)',
  color: 'var(--inv)',
  border: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
  cursor: 'pointer',
  fontSize: 13,
  fontWeight: 700,
});

export const nameBtnStyle = (drivingMode) => ({
  minWidth: drivingMode ? 88 : 64,
  minHeight: drivingMode ? 56 : 44,
  padding: '0 14px',
  borderRadius: 12,
  background: 'var(--card)',
  color: 'var(--t1)',
  border: '1px solid var(--bdr)',
  cursor: 'pointer',
  fontSize: 13,
  fontWeight: 600,
});

export const errorStripStyle = {
  padding: '8px 16px',
  background: 'var(--bad-tint)',
  color: 'var(--bad)',
  fontSize: 13,
  textAlign: 'center',
};

export const pendingPillStyle = {
  margin: '8px auto',
  padding: '8px 14px',
  background: 'var(--warn-tint)',
  color: 'var(--warn)',
  borderRadius: 999,
  fontSize: 13,
  fontWeight: 600,
  display: 'inline-flex',
  gap: 10,
  alignItems: 'center',
};
